import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Icons,
  IconButton,
  iconSize,
  type LucideIcon,
  Menu,
  MenuItem,
  palette,
  Stack,
  Tooltip,
  Typography,
  fontStacks,
  visuallyHidden,
} from '@munifocus/ui';
import {
  INBOX_TAB_CATEGORIES,
  INBOX_TAB_VALUES,
  type InboxTabId,
  type InboxUnreadCountsDto,
  type NotificationCategoryId,
  type NotificationInboxItemDto,
} from '@munifocus/contracts-common';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import {
  inPortalPath,
  useInboxTab,
  useSetRead,
  useUnreadCounts,
  useWatching,
} from '../lib/teamwork-api';
import { useMarkAllRead } from '../lib/notifications-api';
import { intlLocale, timeAgo } from '../lib/relative-time';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * A notification filter, or the list of files the officer follows.
 *
 * "Following" is not a filter over notifications at all — it lists files, not
 * events — so it lives in the overflow menu under its own name ("Files you
 * follow") rather than as a seventh tab beside "File updates", which is the
 * notifications *about* those files. The two used to be called "Followed
 * files" and "Following", and nobody could tell them apart.
 */
type View = InboxTabId | 'following';

/** The filters an officer reaches for every day; the rest sit behind "More". */
const PRIMARY_TABS: readonly InboxTabId[] = ['all', 'mentions', 'assignments', 'deadlines'];
const OVERFLOW_TABS: readonly InboxTabId[] = INBOX_TAB_VALUES.filter(
  (tab) => !PRIMARY_TABS.includes(tab),
);

const CATEGORY_ICON: Record<NotificationCategoryId, LucideIcon> = {
  assignments: Icons.UserCheck,
  deadlines: Icons.Clock,
  'site-visits': Icons.MapPin,
  'case-progress': Icons.FileText,
  escalations: Icons.Gavel,
  correspondence: Icons.Mail,
  training: Icons.GraduationCap,
  account: Icons.KeyRound,
  safety: Icons.ShieldAlert,
  mentions: Icons.AtSign,
  watching: Icons.Eye,
  handovers: Icons.ArrowRightLeft,
};

/** Unread count for a tab: the sum over the categories it shows. */
export function tabUnread(tab: InboxTabId, counts: InboxUnreadCountsDto | undefined): number {
  if (!counts) return 0;
  if (tab === 'all') return counts.total;
  return INBOX_TAB_CATEGORIES[tab].reduce((sum, c) => sum + (counts.byCategory[c] ?? 0), 0);
}

/** One pill in the filter row. Tab semantics, so arrow-free tabbing still reads as a set. */
function FilterPill({
  selected,
  label,
  count,
  onClick,
  endIcon,
  ...aria
}: {
  selected: boolean;
  label: string;
  count?: number;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  endIcon?: ReactNode;
  role?: string;
  'aria-selected'?: boolean;
  'aria-label'?: string;
  'aria-haspopup'?: 'menu';
  'aria-expanded'?: boolean;
  'aria-pressed'?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      {...aria}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        flexShrink: 0,
        height: 34,
        px: 1.5,
        borderRadius: 17,
        border: `1px solid ${selected ? palette.brand.accent : palette.border.interactive}`,
        backgroundColor: selected ? palette.brand.accent : palette.background.paper,
        color: selected ? palette.text.onDark : palette.text.primary,
        font: 'inherit',
        fontFamily: fontStacks.body,
        fontSize: 13.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        transition: 'background-color 200ms, border-color 200ms, color 200ms, transform 200ms',
        '&:hover': {
          borderColor: palette.brand.accent,
          backgroundColor: selected ? palette.brand.accent : palette.brand.accentTint,
        },
        '&:active': { transform: 'scale(0.98)' },
      }}
    >
      {label}
      {count !== undefined && count > 0 && (
        <Box
          component="span"
          aria-hidden
          sx={{
            minWidth: 20,
            px: 0.6,
            borderRadius: 10,
            fontSize: 11.5,
            fontWeight: 700,
            lineHeight: '20px',
            textAlign: 'center',
            backgroundColor: selected ? palette.text.onDark : palette.brand.accentTint,
            color: palette.brand.accent,
          }}
        >
          {count > 99 ? '99+' : count}
        </Box>
      )}
      {endIcon}
    </Box>
  );
}

/** The flat row layout every list on this screen shares. */
const rowSx = {
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  px: { xs: 1, sm: 1.5 },
  py: 1.5,
  borderBottom: `1px solid ${palette.border.line}`,
  transition: 'background-color 200ms',
  '&:hover': { backgroundColor: palette.background.paper },
  '&:focus-within': { backgroundColor: palette.background.paper },
};

/**
 * The row's main action, stretched over the whole row with `::after` so the
 * entire row is the target, while the read toggle on the right stays a
 * separate control rather than a button nested inside a button.
 */
const stretchedSx = {
  flex: 1,
  minWidth: 0,
  appearance: 'none',
  border: 'none',
  background: 'none',
  textAlign: 'left' as const,
  font: 'inherit',
  color: 'inherit',
  p: 0,
  cursor: 'pointer',
  '&::after': { content: '""', position: 'absolute', inset: 0 },
  '&:focus-visible': { outline: 'none' },
  '&:focus-visible::after': {
    outline: `2px solid ${palette.brand.accent}`,
    outlineOffset: -2,
    borderRadius: 1,
  },
};

function InboxRow({ item }: { item: NotificationInboxItemDto }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setRead = useSetRead();
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const path = inPortalPath(item.linkUrl);
  const Icon = CATEGORY_ICON[item.category as NotificationCategoryId] ?? Icons.Bell;
  const toggleLabel = item.read
    ? t('gov.teamwork.inbox.markUnread')
    : t('gov.teamwork.inbox.markRead');

  return (
    <Box component="li" sx={rowSx}>
      {/* Unread dot, then the category glyph: two things the eye can scan down. */}
      <Box
        aria-hidden
        sx={{
          mt: 1.6,
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: item.read ? 'transparent' : palette.brand.accent,
        }}
      />
      <Box
        aria-hidden
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: item.read ? palette.background.default : palette.brand.accentTint,
          color: item.read ? palette.text.muted : palette.brand.accent,
        }}
      >
        <Icon size={iconSize.md} strokeWidth={1.75} />
      </Box>
      <Box
        component="button"
        type="button"
        onClick={() => {
          if (!item.read) setRead.mutate({ id: item.id, read: true });
          if (path) navigate(path);
        }}
        sx={stretchedSx}
      >
        <Stack direction="row" sx={{ gap: 1, alignItems: 'baseline' }}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: item.read ? 500 : 700,
              color: palette.text.primary,
            }}
          >
            {/* The shared `visuallyHidden`, not a local copy: the copy here
                wrote `width: 1, height: 1` in `sx`, which MUI reads as 100%,
                so on every unread row an absolutely positioned span as wide
                as the row started after the icon and ran past the page's
                right edge — the inbox's horizontal scrollbar. */}
            {!item.read && (
              <Box component="span" sx={visuallyHidden}>
                {t('gov.teamwork.inbox.unreadPrefix')}{' '}
              </Box>
            )}
            {item.title[lang]}
          </Typography>
          <Typography
            variant="caption"
            component="span"
            sx={{ flexShrink: 0, color: palette.text.muted, whiteSpace: 'nowrap' }}
          >
            {timeAgo(item.createdAt, i18n.language, t('gov.teamwork.inbox.justNow'))}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: palette.text.secondary,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.body[lang]}
        </Typography>
        <Typography variant="caption" sx={{ color: palette.text.muted }}>
          {t(`common.notifications.categories.${item.category}`)}
        </Typography>
      </Box>
      <Tooltip title={toggleLabel}>
        <IconButton
          size="small"
          aria-label={toggleLabel}
          onClick={() => setRead.mutate({ id: item.id, read: !item.read })}
          // Above the stretched link, so it gets its own clicks.
          sx={{ position: 'relative', zIndex: 1, mt: 0.25, color: palette.text.muted }}
        >
          {item.read ? <Icons.Mail size={iconSize.sm} /> : <Icons.MailOpen size={iconSize.sm} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function EmptyState({
  icon: Icon,
  title,
  body,
}: {
  icon: LucideIcon;
  title: string;
  body?: string;
}) {
  return (
    <Stack sx={{ alignItems: 'center', textAlign: 'center', py: { xs: 6, sm: 8 }, px: 2, gap: 1 }}>
      <Box
        aria-hidden
        sx={{
          width: 56,
          height: 56,
          mb: 1,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: palette.brand.accentTint,
          color: palette.brand.accent,
        }}
      >
        <Icon size={iconSize.xl} strokeWidth={1.75} />
      </Box>
      <Typography sx={{ fontFamily: fontStacks.display, fontWeight: 700, fontSize: 17 }}>
        {title}
      </Typography>
      {body && (
        <Typography variant="body2" sx={{ color: palette.text.secondary, maxWidth: '46ch' }}>
          {body}
        </Typography>
      )}
    </Stack>
  );
}

function Loading() {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={22} aria-label={t('gov.teamwork.inbox.loading')} />
    </Box>
  );
}

const listSx = {
  listStyle: 'none',
  p: 0,
  m: 0,
  borderTop: `1px solid ${palette.border.line}`,
};

function FollowingList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const watching = useWatching();
  const locale = intlLocale(i18n.language);

  if (watching.isLoading) return <Loading />;
  if (watching.isError) return <Alert severity="error">{t('gov.teamwork.inbox.loadError')}</Alert>;
  if (!watching.data?.length) {
    return (
      <EmptyState
        icon={Icons.Eye}
        title={t('gov.teamwork.inbox.followingEmptyTitle')}
        body={t('gov.teamwork.inbox.followingEmpty')}
      />
    );
  }
  return (
    <Box component="ul" sx={listSx}>
      {watching.data.map((f) => (
        <Box component="li" key={f.fileId} sx={rowSx}>
          <Box
            aria-hidden
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.background.default,
              color: palette.text.muted,
            }}
          >
            <Icons.FileText size={iconSize.md} strokeWidth={1.75} />
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => navigate(`/files/${f.fileId}`)}
            sx={stretchedSx}
          >
            <Typography variant="body2" sx={{ fontFamily: fontStacks.mono, fontWeight: 700 }}>
              {f.reference}
            </Typography>
            <Typography variant="body2" sx={{ color: palette.text.secondary }}>
              {f.address}
            </Typography>
            <Typography variant="caption" sx={{ color: palette.text.muted }}>
              {f.lastActivity
                ? t('gov.teamwork.inbox.lastActivity', {
                    action: t(`gov.activity.actions.${f.lastActivity.action.replace(/\./g, '_')}`, {
                      defaultValue: t('gov.activity.unknownAction'),
                    }),
                    when: frenchFirstOfMonth(
                      new Intl.DateTimeFormat(locale, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      }).format(new Date(f.lastActivity.at)),
                    ),
                  })
                : t('gov.teamwork.inbox.noActivity')}
            </Typography>
          </Box>
          <Icons.ChevronRight
            size={iconSize.md}
            aria-hidden
            style={{ flexShrink: 0, alignSelf: 'center', color: palette.text.muted }}
          />
        </Box>
      ))}
    </Box>
  );
}

function NotificationList({ tab, unreadOnly }: { tab: InboxTabId; unreadOnly: boolean }) {
  const { t } = useTranslation();
  const inbox = useInboxTab(tab, unreadOnly);

  if (inbox.isLoading) return <Loading />;
  if (inbox.isError) return <Alert severity="error">{t('gov.teamwork.inbox.loadError')}</Alert>;
  if (!inbox.data?.items.length) {
    return unreadOnly ? (
      <EmptyState
        icon={Icons.CheckCheck}
        title={t('gov.teamwork.inbox.emptyUnread')}
        body={t('gov.teamwork.inbox.emptyUnreadBody')}
      />
    ) : (
      <EmptyState
        icon={Icons.Inbox}
        title={t('gov.teamwork.inbox.empty')}
        body={t(`gov.teamwork.inbox.emptyBody.${tab}`)}
      />
    );
  }
  return (
    <Box component="ul" sx={listSx}>
      {inbox.data.items.map((item) => (
        <InboxRow key={item.id} item={item} />
      ))}
    </Box>
  );
}

/**
 * The unified inbox: mentions, assignments, deadlines, handovers and updates on
 * followed files, one flat list over the same rows as the bell — so reading
 * something here reads it there. A compact filter sits above the list; the
 * files the officer follows are a separate view under "More".
 */
export function Inbox() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const view: View =
    raw === 'following' || (INBOX_TAB_VALUES as readonly string[]).includes(raw ?? '')
      ? (raw as View)
      : 'all';
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const counts = useUnreadCounts();
  const markAll = useMarkAllRead();

  const name = (v: View) => t(`gov.teamwork.inbox.tabs.${v}`);
  const label = (tab: InboxTabId) => {
    const n = tabUnread(tab, counts.data);
    return n > 0 ? `${name(tab)} (${n})` : name(tab);
  };
  const go = (next: View) => {
    setMoreAnchor(null);
    setParams(next === 'all' ? {} : { tab: next });
  };
  // A view picked from the overflow stays visible as a selected pill, so the
  // list is never filtered by something the officer cannot see.
  const pinned = view !== 'all' && !PRIMARY_TABS.includes(view as InboxTabId) ? view : null;
  const overflowUnread = OVERFLOW_TABS.reduce((sum, tab) => sum + tabUnread(tab, counts.data), 0);
  const unread = counts.data?.total ?? 0;

  return (
    <AppShell>
      <PageIntro>{t('gov.teamwork.inbox.subtitle')}</PageIntro>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ gap: 1.5, mb: 2, alignItems: { md: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack
          direction="row"
          role="tablist"
          aria-label={t('gov.teamwork.inbox.tabsLabel')}
          sx={{
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            // Allowed to shrink so the pills wrap, rather than pushing the
            // controls beside them out past the page edge.
            minWidth: 0,
          }}
        >
          {PRIMARY_TABS.map((tab) => (
            <FilterPill
              key={tab}
              role="tab"
              aria-selected={view === tab}
              aria-label={label(tab)}
              selected={view === tab}
              label={name(tab)}
              count={tabUnread(tab, counts.data)}
              onClick={() => go(tab)}
            />
          ))}
          {pinned && (
            <FilterPill
              role="tab"
              aria-selected
              aria-label={pinned === 'following' ? name(pinned) : label(pinned)}
              selected
              label={name(pinned)}
              count={pinned === 'following' ? undefined : tabUnread(pinned, counts.data)}
              onClick={() => go(pinned)}
            />
          )}
          <FilterPill
            selected={false}
            label={t('gov.teamwork.inbox.more')}
            count={overflowUnread}
            aria-haspopup="menu"
            aria-expanded={Boolean(moreAnchor)}
            aria-label={
              overflowUnread > 0
                ? `${t('gov.teamwork.inbox.more')} (${overflowUnread})`
                : t('gov.teamwork.inbox.more')
            }
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            endIcon={<Icons.ChevronDown size={iconSize.sm} aria-hidden />}
          />
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 240, borderRadius: 2, mt: 0.5 } } }}
          >
            {OVERFLOW_TABS.map((tab) => (
              <MenuItem key={tab} selected={view === tab} onClick={() => go(tab)}>
                {label(tab)}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem selected={view === 'following'} onClick={() => go('following')}>
              {name('following')}
            </MenuItem>
          </Menu>
        </Stack>

        {view !== 'following' && (
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilterPill
              selected={unreadOnly}
              aria-pressed={unreadOnly}
              label={t('gov.teamwork.inbox.unreadOnly')}
              onClick={() => setUnreadOnly((v) => !v)}
            />
            {/* Only when there is something to mark: a permanently disabled
                button floating at the edge read as broken. */}
            {unread > 0 && (
              <Button
                size="small"
                variant="text"
                startIcon={<Icons.CheckCheck size={iconSize.sm} />}
                disabled={markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                {t('common.notifications.markAllRead')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {view === 'following' ? (
        <FollowingList />
      ) : (
        <NotificationList tab={view} unreadOnly={unreadOnly} />
      )}
    </AppShell>
  );
}
